<template>
  <div id="coachSelection">
    <!-- Header area -->
    <v-img
      :max-height="$vuetify.breakpoint.lgAndUp ? '715' : null"
      src="~@/assets/coach-profile/select_a_coach_bg.jpg"
      lazy-src="~@/assets/coach-profile/coach-profile-banner-thumb.jpg"
    >
      <TopBar />

      <v-container
        style="max-width: 1000px;"
      >
        <div class="intro mb-10 white--text">
          <h1 class="mb-6">
            Select a Coach
          </h1>
          <p>
            The Global Coach Group community is dedicated to support leaders in achieving positive,
            sustainable growth in their leadership effectiveness.
            Our premium network of more than 2500 certified coaches using
            the 360 Feedforward Coaching process is available for you and your organization.
          </p>
          <p>
            Search your coach by location, level of certification,
            or name below and feel free to contact and make arrangements with them directly.
          </p>
          <p>
            If you are interested in our coaching services for multiple leaders
            or would like for us to recommend coaches please contact us at
            <a
              href="mailto:Support@GlobalCoachGroup.com"
              class="white--text"
            >Support@GlobalCoachGroup.com</a>.
          </p>
        </div>

        <div class="filters mb-10">
          <v-card
            class="mx-auto"
            max-width="800"
          >
            <v-card-text>
              <!--<v-row ref="certificationRow">
              &lt;!&ndash; Certification id &ndash;&gt;
              &lt;!&ndash;<v-col
                cols="12"
                sm="6"
              >
                <v-autocomplete
                  v-model="filter.cert_id"
                  :items="certificationList"
                  item-text="name"
                  item-value="id"
                  label="Certification"
                  outlined
                  dense
                  hide-details
                  clearable
                />
              </v-col>&ndash;&gt;

              &lt;!&ndash; Certification level &ndash;&gt;
              &lt;!&ndash;<v-col
                cols="12"
                sm="6"
              >
                <v-autocomplete
                  v-model="filter.cert_level"
                  :items="levelList"
                  item-text="name"
                  item-value="id"
                  label="Certification level"
                  outlined
                  dense
                  hide-details
                  clearable
                />
              </v-col>&ndash;&gt;

              <v-col cols="12">
                <v-autocomplete
                  v-model="filter.cert_level"
                  :items="levelList"
                  item-text="name"
                  item-value="id"
                  label="Certification level"
                  outlined
                  dense
                  hide-details
                  clearable
                />
              </v-col>
            </v-row>-->
              <v-row ref="locationRow">
                <!-- Country -->
                <v-col
                  cols="12"
                  md="4"
                >
                  <v-autocomplete
                    v-model="filter.country"
                    :items="countryList"
                    item-text="name"
                    item-value="id"
                    label="Country"
                    outlined
                    dense
                    hide-details
                    clearable
                    @change="initLocation('country')"
                  />
                </v-col>

                <!-- Region -->
                <v-col
                  cols="12"
                  sm="6"
                  md="4"
                >
                  <v-autocomplete
                    v-model="filter.region"
                    :items="regionList"
                    item-text="name"
                    item-value="id"
                    label="Region/State/Province"
                    outlined
                    dense
                    hide-details
                    clearable
                    :disabled="!regionList.length"
                    @change="initLocation('region')"
                  />
                </v-col>

                <!-- City -->
                <v-col
                  cols="12"
                  sm="6"
                  md="4"
                >
                  <v-autocomplete
                    v-model="filter.city"
                    :items="cityList"
                    item-text="name"
                    item-value="id"
                    label="City"
                    outlined
                    dense
                    hide-details
                    clearable
                    :disabled="!cityList.length"
                  />
                </v-col>
              </v-row>

              <v-divider class="my-2" />

              <v-row ref="searchRow">
                <!-- Search text -->
                <v-col cols="12">
                  <v-text-field
                    v-model="filter.search"
                    label="Name"
                    outlined
                    dense
                    hide-details
                    clearable
                    append-icon="search"
                    @click:append="loadCoaches"
                    @keyup.enter="loadCoaches"
                  />
                </v-col>
              </v-row>

              <div class="d-flex justify-center my-2">
                <v-btn
                  color="secondary lighten-1"
                  rounded
                  width="140"
                  @click="[scrollToList(), loadCoaches()]"
                >
                  Search
                </v-btn>
              </div>
            </v-card-text>
          </v-card>
        </div>
      </v-container>
    </v-img>

    <!-- Coach area -->
    <v-container
      style="padding-bottom: 50px; max-width: 1000px;"
    >
      <div id="coachList">
        <!-- Upper position pagination -->
        <v-pagination
          v-if="count && pageLength > 1"
          v-model="page"
          class="mb-5"
          total-visible="10"
          :length="pageLength"
          :disabled="isLoadingList"
        />
        <v-row v-if="isLoadingList">
          <v-col
            v-for="index in 4"
            :key="index"
            cols="12"
            sm="6"
          >
            <v-skeleton-loader
              class="profileSkeleton"
              height="225"
              type="avatar, paragraph"
            />
          </v-col>
        </v-row>

        <v-row v-else>
          <template v-if="coaches && coaches.length">
            <v-col
              v-for="(coach, key) in coaches"
              :key="key"
              cols="12"
              sm="6"
              class="d-flex align-center"
            >
              <div
                class="d-flex align-center py-3"
                :class="!$vuetify.breakpoint.smAndUp ? 'justify-center' : null"
              >
                <v-avatar
                  size="150"
                  :color="!coach.profile_image ? 'teal' : ''"
                >
                  <v-img
                    v-if="coach.profile_image"
                    :src="coach.profile_image"
                    :lazy-src="coach.profile_image"
                    alt="Profile"
                  >
                    <template #placeholder>
                      <v-row
                        class="fill-height ma-0"
                        align="center"
                        justify="center"
                      >
                        <v-progress-circular
                          indeterminate
                          color="grey lighten-5"
                        />
                      </v-row>
                    </template>
                  </v-img>
                  <span
                    v-else
                    class="white--text display-2"
                  >
                    <span class="brandFont">
                      {{ coach | initials }}
                    </span>
                  </span>
                </v-avatar>

                <div class="px-4">
                  <div class="font-weight-bold mt-3">
                    {{ coach.first_name }} {{ coach.last_name }}
                  </div>

                  <div>
                    <v-btn
                      class="text-capitalize mt-2"
                      color="secondary lighten-1"
                      rounded
                      outlined
                      @click="viewProfile(coach)"
                    >
                      View Profile
                    </v-btn>
                    <!--<a
                      :href="`${coach.host_url}/${coach.url_index}`"
                      target="_blank"
                    >
                      View Profile
                    </a>-->
                  </div>
                </div>
              </div>
            </v-col>
          </template>

          <v-col
            v-else
            cols="12"
            class="font-italic"
            style="text-align: center;"
          >
            No coaches to display
          </v-col>
        </v-row>

        <!-- Lower position pagination -->
        <v-pagination
          v-if="count && pageLength > 1"
          v-model="page"
          class="my-5"
          total-visible="10"
          :length="pageLength"
          :disabled="isLoadingList"
          @input="scrollToList"
        />
      </div>
    </v-container>
  </div>
</template>

<script>
import coachApi from '@/api/coach';
import TopBar from './layout/TopBar.vue';

export default {
  name: 'CoachSelection',

  components: {
    TopBar,
  },

  filters: {
    initials(coach) {
      if (!coach.first_name && !coach.last_name) return 'X_X';
      let init1 = '';
      let init2 = '';
      if (coach.first_name) {
        init1 = coach.first_name[0].substring(0, 1).toUpperCase();
      }
      if (coach.last_name) {
        init2 = coach.last_name[0].substring(0, 1).toUpperCase();
      }
      return `${init1} ${init2}`;
    },
  },

  data: () => ({
    coaches: [],
    isLoadingList: false,
    countryList: [],
    regionList: [],
    cityList: [],
    // certificationList: [],
    // levelList: [],
    certificationList: [
      {
        id: 'cert_one',
        name: 'Certification One',
      },
      {
        id: 'cert_two',
        name: 'Certification Two',
      },
      {
        id: 'cert_three',
        name: 'Certification Three',
      },
    ],
    levelList: [
      {
        id: 1,
        name: 'Certified Coach',
      },
      {
        id: 2,
        name: 'Advanced Coach',
      },
      {
        id: 3,
        name: 'Master Coach',
      },
    ],
    filter: {
      country: null,
      region: null,
      city: null,
      cert_id: null,
      // cert_id: 'indiv_coaching',
      cert_level: null,
      search: null,
    },
    page: 1,
    count: 0,
    pageLength: 1,
  }),

  watch: {
    page() {
      this.loadCoaches('page');
    },

    'filter.country': {
      deep: true,
      handler(newValue) {
        if (newValue) {
          this.loadRegions(newValue);
        } else {
          this.initLocation('country');
        }
      },
    },

    'filter.region': {
      deep: true,
      handler(newValue) {
        if (newValue) {
          this.loadCities(newValue, 'init');
        } else {
          this.initLocation('region');
        }
      },
    },
  },

  created() {
    this.loadCountries();
    this.loadCoaches();
  },

  methods: {
    initLocation(option) {
      if (option === 'country') {
        this.filter.region = null;
        this.filter.city = null;
        this.regionList = [];
        this.cityList = [];
      } else if (option === 'region') {
        this.filter.city = null;
        this.cityList = [];
      }
    },

    async loadCoaches(option) {
      this.isLoadingList = true;
      if (!option || option !== 'page') {
        this.page = 1;
      }
      try {
        const params = {
          page: this.page,
          page_size: 20,
        };

        // Temp. TODO Enforce Certification ID when Cert.Lv. exist
        if (this.filter.cert_level) {
          this.filter.cert_id = 'indiv_coaching';
        } else {
          this.filter.cert_id = null;
        }

        Object.keys(this.filter).forEach((el) => {
          if (this.filter[el]) {
            params[el] = this.filter[el];
          }
        });
        const response = await coachApi.getCoachList(params);
        this.coaches = (response.results || []).slice().sort((a, b) => {
          const aHas = a.profile_image ? 0 : 1;
          const bHas = b.profile_image ? 0 : 1;
          return aHas - bHas;
        });
        this.count = response.count;
        if (this.count) {
          this.pageLength = Math.ceil(this.count / 20);
        } else {
          this.pageLength = 1;
        }
      } catch (err) {
        console.log(err);
        this.$toast.error(err.msg || err.message);
      } finally {
        this.isLoadingList = false;
      }
    },

    async loadCountries() {
      try {
        this.countryList = await coachApi.getCoachCountries();
      } catch (error) {
        console.log(error);
      }
    },

    async loadRegions(value) {
      try {
        this.regionList = await coachApi.getCoachRegions(value);
      } catch (error) {
        console.log(error);
      }
    },

    async loadCities(value) {
      try {
        this.cityList = await coachApi.getCoachCities(value);
      } catch (error) {
        console.log(error);
      }
    },

    scrollToList() {
      this.$vuetify.goTo('#coachList', {
        duration: 1000,
        easing: 'easeInOutCubic',
      });
    },

    viewProfile(coach) {
      const route = this.$router.resolve({
        name: 'coachProfile',
        params: {
          url_index: coach.profile.url_index,
        },
      });
      window.open(route.href, '_blank');
    },
  },
};
</script>

<style lang="scss">
#coachSelection {
  .profileSkeleton {
    .v-skeleton-loader__avatar {
      margin: 0 auto;
      margin-bottom: 10px;
      height: 150px;
      width: 150px;
    }

    .v-skeleton-loader__paragraph {
      .v-skeleton-loader__text {
        margin: 0 auto;
        margin-bottom: 6px;
      }
    }
  }
}
</style>
